import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const SIZE_CLASS = {
  sm: "max-w-md",   // ~448px
  md: "max-w-lg",   // ~512px
  lg: "max-w-2xl",  // ~672px
  xl: "max-w-3xl",  // ~768px
  full: "max-w-5xl",
};

/**
 * Modal — a single home for every ad-hoc dialog in the app.
 *
 *   <Modal open={...} onClose={...} title="Edit user" size="xl"
 *          footer={<><button>Cancel</button><button>Save</button></>}>
 *     ...form fields...
 *   </Modal>
 *
 * Features:
 *  - Portal-mounted on document.body so positioning is viewport-relative,
 *    not constrained by any ancestor `transform`/`filter`/`contain`.
 *  - ESC closes (unless `closeOnEscape={false}`).
 *  - Click-on-backdrop closes (unless `closeOnBackdrop={false}`).
 *  - Body scroll-lock while open.
 *  - Sticky header/footer; body scrolls internally when content > 90vh.
 *
 * Note: ConfirmDialog (shadcn AlertDialog) intentionally stays separate — it
 * has its own promise-based API. Use this Modal for everything else.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  size = "md",
  footer,
  closeOnEscape = true,
  closeOnBackdrop = true,
  children,
  "data-testid": testId,
}) {
  // ESC + body scroll-lock
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (closeOnEscape && e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  const backdropClick = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
      onMouseDown={backdropClick}
      data-testid={testId}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${SIZE_CLASS[size] || SIZE_CLASS.md} max-h-[90vh] flex flex-col fade-up overflow-hidden my-6`}
        role="dialog"
        aria-modal="true"
      >
        {(title || onClose) && (
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 gap-4">
            <div className="min-w-0 flex items-start gap-3">
              {Icon && <Icon size={18} className="text-slate-400 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                {title && <div className="qb-section-title text-base truncate" data-testid="modal-title">{title}</div>}
                {subtitle && <div className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">{subtitle}</div>}
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 text-xl leading-none -m-1 p-1"
                aria-label="Close"
                data-testid="modal-close"
              ><X size={18} /></button>
            )}
          </div>
        )}
        <div className="p-6 overflow-y-auto flex-1" data-testid="modal-body">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 shrink-0" data-testid="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
